import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '@/lib/api';
import { Project, ApiProject } from '@/types';

/**
 * Project Service
 * 处理所有项目相关的API调用
 */

/**
 * Transform API project format to frontend Project format
 */
function transformApiProject(apiProject: ApiProject): Project {
  return {
    id: apiProject.projectId,
    name: apiProject.projectName,
    description: apiProject.description,
    instructions: apiProject.instructions,
    starred: apiProject.starred,
    archived: apiProject.archived,
    role: apiProject.role,
    createdAt: apiProject.createdAt,
    updatedAt: apiProject.updatedAt,
    lastVisitedAt: apiProject.lastVisitedAt,
  };
}

/**
 * 获取项目列表
 */
export async function fetchProjects(): Promise<Project[]> {
  const res = await apiGet<any>('/api/projects');
  const data = res?.data || res || {};
  if (data.code === 200 || data.projects !== undefined) {
    const apiProjects: ApiProject[] = data.projects || [];
    return apiProjects.map(transformApiProject);
  }
  return [];
}
/**
 * 创建新项目
 * Returns the created project in frontend format
 */
export async function createProject(
  projectName: string
): Promise<{ id: string; name: string }> {
  const res = await apiPost<any>('/api/projects', { projectName });
  const data = res?.data || res || {};
  return {
    id: data.projectId,
    name: data.projectName,
  };
}
/**
 * 重命名项目
 */
export async function renameProject(projectId: string, newName: string) {
  const res = await apiPatch(`/api/projects/${projectId}`, {
    name: newName.trim() || null,
  });
  return res;
}
/**
 * 删除项目
 */
export async function deleteProject(projectId: string): Promise<void> {
  const res = await apiDelete(`/api/projects/${projectId}`);
  return res;
}

/**
 * Star/Unstar 项目
 */
export async function toggleStarProject(
  projectId: string,
  starred: boolean
): Promise<{ code?: number; message?: string }> {
  return await apiPost(`/api/projects/${projectId}/star`, { starred });
}

/**
 * Archive/Unarchive 项目
 */
export async function archiveProject(
  projectId: string,
  archived: boolean
): Promise<void> {
  await apiPost(`/api/projects/${projectId}/archive`, { archived });
}
/**
 * 上传项目文件（使用 axios 支持进度跟踪）
 */
export async function uploadProjectFiles(
  projectId: string,
  files: File[],
  socketId: string,
  onProgress?: (fileIndex: number, progress: number) => void
): Promise<void> {
  const uploadPromises = files.map(async (file, index) => {
    const formData = new FormData();
    formData.append('files', file);
    formData.append('sid', socketId);
    await apiUpload(`/api/projects/${projectId}/files`, formData, progress => {
      if (onProgress) {
        onProgress(index, progress);
      }
    });
  });
  await Promise.all(uploadPromises);
}
